// Tách gói theo trang / lớp phủ: thành phần nạp mã khi cần, có preload() để nạp trước (router gọi `component.preload` khi
// điều hướng và khi rê chuột lên liên kết — defaultPreload 'intent'; OverlayHost nạp trước lớp phủ lúc rảnh). Đã nạp ⇒ dựng
// ngay, không qua Suspense; chưa nạp ⇒ treo (Suspense gần nhất) tới khi nạp xong. Nạp lỗi: lần nạp nền (router rê chuột,
// nạp lúc rảnh — lỗi bị nuốt) chưa lần dựng nào treo trên nó ⇒ lần dựng sau nạp lại một lần; lần nạp mà lần dựng đã treo trên
// đó lỗi ⇒ dựng ném lỗi cho error boundary (giữ nguyên promise đã lỗi: tạo promise mới mỗi lần dựng thì React treo / nạp lại
// mãi, lỗi không tới boundary); preload() gọi sau đó (router điều hướng, nút "Thử lại" của lớp phủ) nạp lại. Chrome giữ lỗi
// import động của cùng URL trong trang (import lại trả lỗi ngay) ⇒ lỗi tải chunk chỉ khắc phục được bằng tải lại trang
// (useStaleChunkReload ở error boundary).
import { createElement, use, type ComponentType, type ReactElement } from 'react';

/** Thành phần hàm nạp mã khi cần (dùng được làm `component` của route TanStack — router gọi `preload`). */
export type Preloadable<P extends object = object> = ((props: P) => ReactElement) & { preload: () => Promise<void>; displayName?: string };

export function lazyComponent<P extends object, M>(load: () => Promise<M>, pick: (m: M) => ComponentType<P>, name = 'Lazy'): Preloadable<P> {
  let Loaded: ComponentType<P> | null = null;
  let pending: Promise<void> | null = null;
  let failed = false;
  /** Promise mà lần dựng gần nhất đã treo trên đó. */
  let rendered: Promise<void> | null = null;
  const preload = () => {
    if (!pending || failed) {
      failed = false;
      pending = load().then(
        (m) => {
          Loaded = pick(m);
        },
        (e: unknown) => {
          failed = true;
          throw e;
        },
      );
    }
    return pending;
  };
  function LazyComponent(props: P): ReactElement {
    if (!Loaded) {
      rendered = !pending || (failed && rendered !== pending) ? preload() : pending;
      use(rendered);
    }
    return createElement(Loaded!, props);
  }
  LazyComponent.displayName = name;
  return Object.assign(LazyComponent, { preload });
}
